type LoginState = 'IDLE' | 'IN_PROGRESS' | 'COMMITTING' | 'COMPLETED' | 'CANCELLED';

type LoginMethod = 'apple' | 'google' | 'biometric' | 'restore' | 'otp';

interface LoginSnapshot {
  userId: string;
  sessionToken?: string;
  campaignId?: string;
  subscriptionStatus?: 'unknown' | 'free' | 'paying';
  loginMethod: LoginMethod;
  committedAt: number;
}

type DeferredAction = (snapshot: LoginSnapshot) => void | Promise<void>;

const TRANSACTION_TIMEOUT_MS = 30000;

class LoginCoordinator {
  private static instance: LoginCoordinator;

  private state: LoginState = 'IDLE';
  private snapshot: LoginSnapshot | null = null;
  private loginMethod: LoginMethod | null = null;
  private transactionStartTime: number = 0;

  private pendingPromises: Array<{
    resolve: (snapshot: LoginSnapshot) => void;
    reject: (error: Error) => void;
  }> = [];

  private actionQueue: Map<string, DeferredAction> = new Map();
  private timeoutTimer: NodeJS.Timeout | null = null;

  private constructor() {
    console.log('[LoginCoordinator] Instance created');
  }

  static getInstance(): LoginCoordinator {
    if (!LoginCoordinator.instance) {
      LoginCoordinator.instance = new LoginCoordinator();
    }
    return LoginCoordinator.instance;
  }

  beginLogin(method: LoginMethod): void {
    if (this.state === 'IN_PROGRESS' || this.state === 'COMMITTING') {
      console.log(`[LoginCoordinator] Transaction already in progress, cancelling previous`);
      this.cancelLoginInternal('New transaction started');
    }

    console.log(`[LoginCoordinator] State: ${this.state} -> IN_PROGRESS (method: ${method})`);

    this.state = 'IN_PROGRESS';
    this.loginMethod = method;
    this.transactionStartTime = Date.now();
    this.snapshot = null;
    this.actionQueue.clear();

    this.startTimeoutTimer();

    this.notifyStabilityGuard(false);
  }

  commitLogin(snapshot: Omit<LoginSnapshot, 'committedAt'>): void {
    if (this.state !== 'IN_PROGRESS') {
      console.warn(`[LoginCoordinator] Cannot commit: state is ${this.state}, expected IN_PROGRESS`);
      return;
    }

    this.clearTimeoutTimer();

    console.log(`[LoginCoordinator] State: IN_PROGRESS -> COMMITTING`);
    this.state = 'COMMITTING';

    const duration = Date.now() - this.transactionStartTime;

    const finalSnapshot: LoginSnapshot = Object.freeze({
      ...snapshot,
      committedAt: Date.now(),
    });

    this.snapshot = finalSnapshot;

    console.log(`[LoginCoordinator] Resolving ${this.pendingPromises.length} pending whenLoginStable promises`);
    for (const { resolve } of this.pendingPromises) {
      try {
        resolve(finalSnapshot);
      } catch (e) {
        console.error('[LoginCoordinator] Error resolving promise:', e);
      }
    }
    this.pendingPromises = [];

    this.flushQueue(finalSnapshot);

    console.log(`[LoginCoordinator] State: COMMITTING -> COMPLETED (duration: ${duration}ms)`);
    this.state = 'COMPLETED';

    this.notifyStabilityGuard(true);
  }

  cancelLogin(reason?: string): void {
    this.cancelLoginInternal(reason);
  }

  private cancelLoginInternal(reason?: string): void {
    if (this.state === 'IDLE' || this.state === 'COMPLETED') {
      console.log(`[LoginCoordinator] Cannot cancel: state is ${this.state}`);
      return;
    }

    this.clearTimeoutTimer();

    const previousState = this.state;
    console.log(`[LoginCoordinator] State: ${previousState} -> CANCELLED (reason: ${reason || 'none'})`);
    this.state = 'CANCELLED';

    const error = new Error(`Login cancelled: ${reason || 'Unknown reason'}`);

    console.log(`[LoginCoordinator] Rejecting ${this.pendingPromises.length} pending whenLoginStable promises`);
    for (const { reject } of this.pendingPromises) {
      try {
        reject(error);
      } catch (e) {
        console.error('[LoginCoordinator] Error rejecting promise:', e);
      }
    }
    this.pendingPromises = [];

    this.actionQueue.clear();

    this.notifyStabilityGuard(true);

    setTimeout(() => {
      if (this.state === 'CANCELLED') {
        console.log(`[LoginCoordinator] State: CANCELLED -> IDLE (auto-reset)`);
        this.state = 'IDLE';
        this.snapshot = null;
        this.loginMethod = null;
      }
    }, 100);
  }

  whenLoginStable(): Promise<LoginSnapshot> {
    if (this.state === 'COMPLETED' && this.snapshot) {
      console.log('[LoginCoordinator] whenLoginStable: Already COMPLETED, returning snapshot immediately');
      return Promise.resolve(this.snapshot);
    }

    if (this.state === 'IN_PROGRESS' || this.state === 'COMMITTING') {
      console.log(`[LoginCoordinator] whenLoginStable: State is ${this.state}, queuing promise`);
      return new Promise((resolve, reject) => {
        this.pendingPromises.push({ resolve, reject });
      });
    }

    if (this.state === 'CANCELLED') {
      console.log('[LoginCoordinator] whenLoginStable: State is CANCELLED, rejecting');
      return Promise.reject(new Error('Login was cancelled'));
    }

    console.log('[LoginCoordinator] whenLoginStable: State is IDLE with no snapshot, rejecting');
    return Promise.reject(new Error('No login in progress'));
  }

  enqueueAction(key: string, action: DeferredAction): void {
    if (this.actionQueue.has(key)) {
      console.log(`[LoginCoordinator] Action replaced: ${key}`);
    } else {
      console.log(`[LoginCoordinator] Action enqueued: ${key}`);
    }
    this.actionQueue.set(key, action);
  }

  private async flushQueue(snapshot: LoginSnapshot): Promise<void> {
    const actionsCount = this.actionQueue.size;
    if (actionsCount === 0) {
      console.log('[LoginCoordinator] No actions to flush');
      return;
    }

    console.log(`[LoginCoordinator] Flushing queue: ${actionsCount} actions`);

    const actions = Array.from(this.actionQueue.entries());
    this.actionQueue.clear();

    for (const [key, action] of actions) {
      try {
        console.log(`[LoginCoordinator] Executing action: ${key}`);
        await action(snapshot);
        console.log(`[LoginCoordinator] Action completed: ${key}`);
      } catch (e) {
        console.error(`[LoginCoordinator] Action failed: ${key}`, e);
      }
    }

    console.log(`[LoginCoordinator] Queue flush complete: ${actionsCount} actions executed`);
  }

  getState(): LoginState {
    return this.state;
  }

  getSnapshot(): LoginSnapshot | null {
    return this.state === 'COMPLETED' ? this.snapshot : null;
  }

  getLoginMethod(): LoginMethod | null {
    return this.loginMethod;
  }

  isLoginInProgress(): boolean {
    return this.state === 'IN_PROGRESS' || this.state === 'COMMITTING';
  }

  shouldDeferAction(): boolean {
    return this.state === 'IN_PROGRESS' || this.state === 'COMMITTING';
  }

  private startTimeoutTimer(): void {
    this.clearTimeoutTimer();

    this.timeoutTimer = setTimeout(() => {
      if (this.state === 'IN_PROGRESS' || this.state === 'COMMITTING') {
        console.warn(`[LoginCoordinator] WARNING: Transaction timeout after ${TRANSACTION_TIMEOUT_MS}ms`);
        this.cancelLoginInternal('Transaction timeout after 30s');
      }
    }, TRANSACTION_TIMEOUT_MS);

    console.log(`[LoginCoordinator] Timeout timer started: ${TRANSACTION_TIMEOUT_MS}ms`);
  }

  private clearTimeoutTimer(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
      console.log('[LoginCoordinator] Timeout timer cleared');
    }
  }

  private notifyStabilityGuard(transactionComplete: boolean): void {
    try {
      const { appStabilityGuard } = require('@/utils/appStabilityGuard');
      if (appStabilityGuard && typeof appStabilityGuard.setLoginTransactionComplete === 'function') {
        appStabilityGuard.setLoginTransactionComplete(transactionComplete);
      }
    } catch (e) {
      console.log('[LoginCoordinator] Could not notify stability guard (not available)');
    }
  }

  reset(): void {
    this.clearTimeoutTimer();

    for (const { reject } of this.pendingPromises) {
      try {
        reject(new Error('LoginCoordinator reset'));
      } catch (e) {}
    }
    this.pendingPromises = [];

    this.actionQueue.clear();
    this.state = 'IDLE';
    this.snapshot = null;
    this.loginMethod = null;
    this.transactionStartTime = 0;

    this.notifyStabilityGuard(true);

    console.log('[LoginCoordinator] Reset complete');
  }
}

export const loginCoordinator = LoginCoordinator.getInstance();

export type { LoginState, LoginMethod, LoginSnapshot, DeferredAction };
